import React, { useCallback, useContext, useMemo, useState } from "react";
import { Calendar } from "react-multi-date-picker";

import { makeStyles } from "@mui/styles";
import { alpha } from "@mui/material/styles";
import {
  Box,
  TextField,
  Dialog,
  IconButton,
  Button,
  Divider,
  MenuItem,
  Tooltip,
  Paper,
  Chip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  Cancel,
  ChangeCircle,
  Close,
  CopyAll,
  DeleteForever,
  Autorenew,
  CleaningServices,
  CalendarMonth,
  Schedule,
  FilterAlt,
  EventNote,
  ArrowForward,
  DoneAll,
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import AlertaContext from "../../../Context/Alerta/AlertaContext";
import Cargando from "../../../components/Extra/Cargando";
import ConfirmacionDialog from "../Dialogs/ConfirmacionDialog";

const useStyles = makeStyles((theme) => ({
  paramSelect: {
    flex: "1 1 150px",
    minWidth: 0,
    "& .MuiInputBase-input": { fontSize: "0.72rem", padding: "6px 10px" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: theme.palette.background.paper,
      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.45),
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}`,
      },
    },
    "& .MuiInputLabel-root": { fontSize: "0.68rem" },
  },
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "0.62rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
  },
  surfaceCard: {
    padding: "10px 12px",
    borderRadius: "14px",
    border: `1px solid ${theme.palette.divider}`,
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.98,
    )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
    boxShadow: `0 1px 3px ${alpha(
      theme.palette.common.black,
      0.05,
    )}, 0 1px 2px ${alpha(theme.palette.common.black, 0.03)}`,
  },
  dateRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: "6px",
    gap: "6px",
    padding: "6px 8px",
    borderRadius: "10px",
    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    borderLeft: "3px solid transparent",
    backgroundColor: alpha(theme.palette.background.paper, 0.55),
    transition: "all 0.18s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
      borderColor: alpha(theme.palette.primary.main, 0.28),
      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
      transform: "translateY(-1px)",
    },
  },
}));

const latino = {
  name: "español",
  months: [
    ["Enero", "Ene"],
    ["Febrero", "Feb"],
    ["Marzo", "Mar"],
    ["Abril", "Abr"],
    ["Mayo", "May"],
    ["Junio", "Jun"],
    ["Julio", "Jul"],
    ["Agosto", "Ago"],
    ["Septiembre", "Sep"],
    ["Octubre", "Oct"],
    ["Noviembre", "Nov"],
    ["Diciembre", "Dic"],
  ],
  weekDays: [
    ["Sabado", "Sab"],
    ["Domingo", "Dom"],
    ["Lunes", "Lun"],
    ["Martes", "Mar"],
    ["Miercoles", "Mie"],
    ["Jueves", "Jue"],
    ["Viernes", "Vie"],
  ],
  digits: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
  meridiems: [
    ["AM", "am"],
    ["PM", "pm"],
  ],
};
const diferenciaTiempo = new Date().getTimezoneOffset() * 60 * 1000;

const Calendario = (props) => {
  if (!props.openCalendario && props.setopenCalendario) {
    return null;
  }
  return props.setopenCalendario ? (
    <Dialog
      open={props.openCalendario}
      onClose={() => {
        props.setopenCalendario(false);
      }}
      PaperProps={{
        sx: {
          width: {
            xs: "95%",
            sm: "92%",
            md: "85%",
            lg: "78%",
          },
          maxWidth: 1100,
          height: {
            xs: "92vh",
            sm: "90vh",
            md: "88vh",
            lg: "88vh",
          },
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      <CalendarioComponent {...props} />
    </Dialog>
  ) : (
    <Box p={0.5} width={"100%"}>
      <CalendarioComponent {...props} />
    </Box>
  );
};

export default Calendario;

export const CalendarioComponent = (props) => {
  const { mostrarAlerta } = useContext(AlertaContext);
  const theme = useTheme();
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 180,
        borderRadius: "8px",
      },
    },
  };
  function getStylesMenuItems(name, permisos, theme) {
    return {
      fontWeight:
        permisos.indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
      backgroundColor:
        permisos.indexOf(name) === -1
          ? theme.palette.background.paper
          : theme.palette.primary.main,
      color:
        permisos.indexOf(name) === -1
          ? theme.palette.text.primary
          : theme.palette.primary.contrastText,
      borderRadius: "6px",
      margin: "2px 5px",
      fontSize: "0.7rem",
    };
  }
  const clases = useStyles();
  const {
    propiedadDB,
    focusedDate,
    setFocusedDate,
    item,
    setitem,
    TextoPropiedad,
    textoPropiedadDB,
    openCalendario,
    setopenCalendario,
    mapDays = ({ date, isSameDate }) => {
      let props = {};
      if (isSameDate(date, focusedDate)) {
        props.style = { backgroundColor: theme.palette.error.main };
      }
      return props;
    },
    filtro,
    readOnly,
    sinValidacionTurno,
    ocultarParametros,
  } = props;

  const timePickerSx = {
    flex: { xs: "1 1 44%", sm: "0 0 auto" },
    width: { sm: 108 },
    "& .MuiOutlinedInput-root": { borderRadius: "8px", paddingRight: "4px" },
    "& .MuiInputBase-input": { fontSize: "0.7rem", padding: "4px 4px 4px 8px" },
    "& .MuiInputLabel-root": { fontSize: "0.62rem" },
    "& .MuiInputAdornment-root": { marginLeft: "2px" },
    "& .MuiIconButton-root": { padding: "3px" },
  };

  const [seccionActivaDia, setseccionActivaDia] = useState({ dia: "todos" });
  const { dia } = seccionActivaDia;

  const mesesAnios = useMemo(() => {
    let currentDate = new Date();
    let months = [
      currentDate.getFullYear() +
        "-" +
        (currentDate.getMonth() + 1).toString().padStart(2, "0"),
    ];
    for (let i = 0; i < 11; i++) {
      let nextMonthDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + i + 1,
        1,
      );
      months.push(
        nextMonthDate.getFullYear() +
          "-" +
          (nextMonthDate.getMonth() + 1).toString().padStart(2, "0"),
      );
    }
    return months;
  }, []);

  const [horasDefault, sethorasDefault] = useState({
    horaDesde: "",
    horaHasta: "",
  });
  const [diasSelect, setdiasSelect] = useState([]);
  const [mesesSelect, setmesesSelect] = useState([]);
  const [turno, setturno] = useState("");
  const [diasFrancoMostrarCampo, setdiasFrancoMostrarCampo] = useState(false);
  const [diasFranco, setDiasFranco] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [openConfirmDialog, setopenConfirmDialog] = useState(false);

  const ocultarCalendario = useCallback(() => {
    setCargando(true);
    setTimeout(() => setCargando(false), 500);
  }, []);

  const [añosInput, setañosInput] = useState(new Date().getFullYear());

  function getDaysInMonth(month, year, resDiasNumber) {
    var date = new Date(year, month, 1);
    var days = [];
    while (date.getMonth() === month) {
      const existe = resDiasNumber.some(
        (diaNumber) => diaNumber === new Date(date).getDay(),
      );
      if (existe) {
        days.push(Date.parse(date));
      }
      date.setDate(date.getDate() + 1);
    }
    return days;
  }
  function areDaysConsecutive(dates) {
    const parsedDates = dates.map((date) => new Date(date.desde).getTime());
    parsedDates.sort((a, b) => a - b);
    for (let i = 1; i < parsedDates.length; i++) {
      const diffInDays =
        (parsedDates[i] - parsedDates[i - 1]) / (1000 * 60 * 60 * 24);
      if (diffInDays !== 1) {
        return true;
      }
    }
    return false;
  }
  function generateWorkDays(propiedadDB, diasFranco, mesesSelect) {
    const workDays = [];
    const startDate = new Date(propiedadDB[0].desde);
    const endDate = new Date(
      mesesSelect[mesesSelect.length - 1] + "-31T23:59:59",
    );
    const workDaysCount = propiedadDB.length;
    const restDaysCount = parseInt(diasFranco, 10);
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      for (let i = 0; i < workDaysCount; i++) {
        if (currentDate > endDate) break;
        const currentMonthYear =
          currentDate.getFullYear() +
          "-" +
          (currentDate.getMonth() + 1).toString().padStart(2, "0");
        if (mesesSelect.includes(currentMonthYear)) {
          workDays.push(currentDate.getTime());
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      currentDate.setDate(currentDate.getDate() + restDaysCount);
    }
    return { days: workDays };
  }

  const parametrizar = () => {
    if (propiedadDB.length === 0 && diasFrancoMostrarCampo) {
      return mostrarAlerta(
        "Deben ser dias consecutivos para esta opción",
        "error",
      );
    }
    if (areDaysConsecutive(propiedadDB) && diasFrancoMostrarCampo) {
      return mostrarAlerta(
        "Deben ser dias consecutivos para esta opción",
        "error",
      );
    }
    if (diasFrancoMostrarCampo) {
      setData(generateWorkDays(propiedadDB, diasFranco, mesesSelect).days);
      return;
    }

    const mesesNumber = {
      ENERO: 0,
      FEBRERO: 1,
      MARZO: 2,
      ABRIL: 3,
      MAYO: 4,
      JUNIO: 5,
      JULIO: 6,
      AGOSTO: 7,
      SEPTIEMBRE: 8,
      OCTUBRE: 9,
      NOVIEMBRE: 10,
      DICIEMBRE: 11,
    };

    const diasNumber = {
      LUNES: 1,
      MARTES: 2,
      MIERCOLES: 3,
      JUEVES: 4,
      VIERNES: 5,
      SABADO: 6,
      DOMINGO: 0,
    };

    const resDiasNumber = diasSelect.map((item) => {
      return diasNumber[item];
    });

    let days = [];
    mesesSelect.forEach((mesSelect) => {
      days.push(
        ...getDaysInMonth(
          Number(mesSelect.split("-")[1]) - 1,
          Number(mesSelect.split("-")[0]),
          resDiasNumber,
        ),
      );
    });
    setData(days);
  };

  const crearFecha = (date, personalizable) => {
    if (date === "") {
      return "";
    }
    if (!date) {
      return "";
    }
    const resParse = isNaN(date) ? Date.parse(date) : date;
    const DateParseDiff = resParse - diferenciaTiempo;
    if (turno === "NOCHE" && personalizable) {
      const resFecha = new Date(
        DateParseDiff + 60 * 60 * 10 * 10 * 10 * 24,
      ).toISOString();
      return resFecha.substring(0, 19);
    }
    const resFecha = new Date(DateParseDiff).toISOString();
    return resFecha.substring(0, 19);
  };

  const setData = (days) => {
    const diasRes = filtro ? filtro(days) : days;
    const resDB = diasRes.map((itemFecha, index) => {
      const itemFDB = propiedadDB.find((itemDB) => {
        return (
          itemDB.desde.substr(0, 10) === crearFecha(itemFecha).substr(0, 10)
        );
      });
      return {
        desde: itemFDB
          ? itemFDB.desde
          : horasDefault.horaDesde === ""
          ? crearFecha(itemFecha)
          : crearFecha(itemFecha).substring(0, 11) +
            horasDefault.horaDesde +
            ":00",
        hasta: itemFDB
          ? itemFDB.hasta
          : horasDefault.horaHasta === ""
          ? crearFecha(itemFecha, true)
          : crearFecha(itemFecha, true).substring(0, 11) +
            horasDefault.horaHasta +
            ":00",
      };
    });

    setitem({
      ...item,
      [TextoPropiedad]: diasRes.map((itemE) => {
        return {
          desde: isNaN(itemE)
            ? Date.parse(itemE)
            : isNaN(Date.parse(itemE))
            ? itemE
            : Date.parse(itemE),
          hasta: "",
        };
      }),
      [textoPropiedadDB]: resDB,
    });
  };

  function validarFecha(itemEdit, fecha) {
    const fechaValidar = new Date(fecha.replace("T", " "));
    const rangos = [
      { nombre: "hayDelMismoDia", ...itemEdit.hayDelMismoDia },
      { nombre: "hayDelMismoNoche", ...itemEdit.hayDelMismoNoche },
      { nombre: "hayDelMismoAmbos", ...itemEdit.hayDelMismoAmbos },
    ];
    for (const rango of rangos) {
      const desde = new Date(rango.desde);
      const hasta = new Date(rango.hasta);
      if (fechaValidar >= desde && fechaValidar <= hasta) {
        return {
          error: `No se puede seleccionar una hora entre ${rango.desde} y ${rango.hasta}`,
        };
      }
    }
    return { ok: true };
  }

  const aplicarHorasATodas = () => {
    if (horasDefault.horaDesde === "" && horasDefault.horaHasta === "") {
      return;
    }
    const res = propiedadDB.map((itemEdit) => {
      const validacion = new Date(itemEdit.desde).getDay();
      if (validacion !== dia && dia !== "todos") {
        return itemEdit;
      }
      return {
        ...itemEdit,
        desde:
          horasDefault.horaDesde === ""
            ? itemEdit.desde
            : itemEdit.desde.substring(0, 11) + horasDefault.horaDesde + ":00",
        hasta:
          horasDefault.horaHasta === ""
            ? itemEdit.hasta
            : itemEdit.hasta.substring(0, 11) + horasDefault.horaHasta + ":00",
      };
    });
    setitem({ ...item, [textoPropiedadDB]: res });
  };

  const diasSemana = [
    { label: "Lu", value: 1 },
    { label: "Ma", value: 2 },
    { label: "Mi", value: 3 },
    { label: "Ju", value: 4 },
    { label: "Vi", value: 5 },
    { label: "Sa", value: 6 },
    { label: "Do", value: 0 },
  ];
  const diaLabel =
    dia === "todos"
      ? "Todos"
      : diasSemana.find((d) => d.value === dia)?.label || "Filtro";

  const isDisabled =
    (diasFrancoMostrarCampo ? diasFranco <= 0 : diasSelect.length === 0) ||
    mesesSelect.length === 0 ||
    turno.length === 0 ||
    horasDefault.horaDesde.length === 0 ||
    horasDefault.horaHasta.length === 0;

  const hayHorasDefault =
    horasDefault.horaDesde !== "" || horasDefault.horaHasta !== "";

  const esDialog = !!setopenCalendario;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <ConfirmacionDialog
        open={openConfirmDialog}
        setopen={setopenConfirmDialog}
        categoria="warning"
        titulo={`¿Esta seguro de reiniciar las fechas?`}
        botones={[
          {
            tooltip: "Se eliminaran los registros",
            texto: "SI",
            funcion: () => {
              setdiasSelect([]);
              setmesesSelect([]);
              setturno("");
              setitem({
                ...item,
                [TextoPropiedad]: [],
                [textoPropiedadDB]: [],
              });
              setopenConfirmDialog(false);
              setDiasFranco(0);
              ocultarCalendario();
            },
            disabled: false,
            Icon: DeleteForever,
            color: "error",
            id: 1,
            ocultar: false,
            tipo: "boton",
            variante: "contained",
            size: "medium",
            sx: { marginRight: ".3rem" },
          },
          {
            tooltip: "Cancelar",
            texto: "NO",
            funcion: () => setopenConfirmDialog(false),
            disabled: false,
            Icon: Cancel,
            color: "secondary",
            id: 2,
            ocultar: false,
            tipo: "boton",
            variante: "contained",
            size: "medium",
            sx: {},
          },
        ]}
      />

      <Box
        p={1}
        sx={{
          ...(esDialog
            ? { flex: "1 1 auto", minHeight: 0, overflowY: "auto" }
            : {}),
          backgroundColor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        {/* ===== Header ===== */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
          pb={1}
          flexShrink={0}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Box display="flex" alignItems="center" gap={0.8}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: "9px",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            >
              <CalendarMonth fontSize="small" />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: 1.1,
                }}
              >
                Calendario de fechas
              </Typography>
              <Box display="flex" alignItems="center" gap={0.4} mt={0.2}>
                {propiedadDB.length > 0 && (
                  <Chip
                    size="small"
                    label={`${propiedadDB.length} fechas`}
                    color="primary"
                    sx={{
                      fontSize: "0.62rem",
                      height: "18px",
                      fontWeight: 600,
                    }}
                  />
                )}
                {turno && (
                  <Chip
                    size="small"
                    label={turno}
                    color={turno === "NOCHE" ? "secondary" : "primary"}
                    variant={turno === "NOCHE" ? "filled" : "outlined"}
                    sx={{
                      fontSize: "0.62rem",
                      height: "18px",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          {setopenCalendario && (
            <Tooltip title="Cerrar" arrow>
              <IconButton
                size="small"
                onClick={() => setopenCalendario(false)}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 1.5,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <Close fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* ===== Parametrización ===== */}
        {!ocultarParametros && (
          <Paper
            className={clases.surfaceCard}
            elevation={0}
            sx={{ mb: 1, flexShrink: 0 }}
          >
            <Box className={clases.sectionLabel} mb={0.8}>
              <Autorenew
                sx={{ fontSize: "0.95rem", color: theme.palette.primary.main }}
              />
              Parametrización rápida
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 400,
                  textTransform: "none",
                  letterSpacing: 0,
                  color: alpha(theme.palette.text.secondary, 0.8),
                  ml: 0.5,
                }}
              >
                Genera fechas por día y mes
              </Typography>
            </Box>
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.6}>
              {diasFrancoMostrarCampo ? (
                <TextField
                  className={clases.paramSelect}
                  size="small"
                  type="number"
                  label="Días en blanco"
                  value={diasFranco}
                  onChange={(e) => setDiasFranco(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Cambiar a selección de días" arrow>
                        <IconButton
                          size="small"
                          sx={{ p: 0.3 }}
                          onClick={() => {
                            setdiasSelect([]);
                            setDiasFranco(0);
                            setdiasFrancoMostrarCampo(false);
                          }}
                        >
                          <ChangeCircle fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              ) : (
                <TextField
                  className={clases.paramSelect}
                  size="small"
                  label="Día"
                  value={diasSelect}
                  onChange={(e) => setdiasSelect(e.target.value)}
                  select
                  MenuProps={MenuProps}
                  SelectProps={{
                    displayEmpty: true,
                    multiple: true,
                    endAdornment: props.mostrarDiasFranco ? (
                      <Tooltip title="Cambiar a días en blanco" arrow>
                        <IconButton
                          size="small"
                          sx={{ p: 0.3 }}
                          onClick={() => {
                            setdiasSelect([]);
                            setDiasFranco(0);
                            setdiasFrancoMostrarCampo(true);
                          }}
                        >
                          <ChangeCircle fontSize="small" color="secondary" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      undefined
                    ),
                    renderValue: (selected) =>
                      selected.map((v) => v.substr(0, 3)).join(", "),
                  }}
                >
                  <MenuItem disabled value="">
                    Seleccione.
                  </MenuItem>
                  {[
                    "LUNES",
                    "MARTES",
                    "MIERCOLES",
                    "JUEVES",
                    "VIERNES",
                    "SABADO",
                    "DOMINGO",
                  ].map((item) => (
                    <MenuItem
                      style={getStylesMenuItems(item, diasSelect, theme)}
                      value={item}
                    >
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                className={clases.paramSelect}
                size="small"
                label="Meses"
                value={mesesSelect}
                onChange={(e) => setmesesSelect(e.target.value)}
                select
                MenuProps={MenuProps}
                SelectProps={{
                  displayEmpty: true,
                  multiple: true,
                  renderValue: (selected) => selected.join(", "),
                }}
              >
                <MenuItem disabled value="">
                  Seleccione.
                </MenuItem>
                {mesesAnios.map((item) => (
                  <MenuItem
                    style={getStylesMenuItems(item, mesesSelect, theme)}
                    value={item}
                  >
                    {item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                className={clases.paramSelect}
                size="small"
                label="Turno"
                value={turno}
                onChange={(e) => setturno(e.target.value)}
                select
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Seleccione.</MenuItem>
                {["DIA", "NOCHE"].map((item) => (
                  <MenuItem value={item}>{item}</MenuItem>
                ))}
              </TextField>
              <Box
                display="flex"
                gap={0.5}
                alignItems="center"
                sx={{
                  flex: { xs: "1 1 100%", sm: "0 0 auto" },
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiButton-root": { flex: { xs: 1, sm: "0 0 auto" } },
                }}
              >
                <Button
                  disabled={isDisabled}
                  variant="contained"
                  color="info"
                  size="small"
                  startIcon={<Autorenew fontSize="small" />}
                  onClick={() => parametrizar()}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    px: 1.2,
                    minHeight: 30,
                    boxShadow: "none",
                  }}
                >
                  {isDisabled ? "Complete los campos" : "Parametrizar"}
                </Button>
                {propiedadDB.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<CleaningServices fontSize="small" />}
                    onClick={() => setopenConfirmDialog(true)}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      px: 1,
                      minHeight: 30,
                    }}
                  >
                    Reiniciar
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {/* ===== Calendario + herramientas ===== */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={0.8}
          flexWrap="wrap"
          mb={1}
          flexShrink={0}
        >
          <Paper
            className={clases.surfaceCard}
            elevation={0}
            sx={{
              p: 1,
            
           
            }}
          >
            <Box className={clases.sectionLabel} mb={0.8} sx={{ px: 0.3 }}>
              <CalendarMonth
                sx={{ fontSize: "0.95rem", color: theme.palette.primary.main }}
              />
              Calendario
            </Box>
            {cargando ? (
              <Cargando titulo={"Cargando..."} />
            ) : (
              <Calendar
                readOnly={readOnly}
                Layout="mobile"
                locale={latino}
                weekStartDayIndex={1}
                numberOfMonths={1}
                value={propiedadDB.map((item) => Date.parse(item.desde))}
                onChange={(e, i) => {
                  const modifiedDates = propiedadDB.map(
                    (item) =>
                      Date.parse(item.desde.substr(0, 10)) +
                      new Date().getTimezoneOffset() * 60 * 1000,
                  );
                  const datesEvent = e.map((item) => Date.parse(item));
                  const dateFind = datesEvent.find(
                    (itemDBDate) => !modifiedDates.includes(itemDBDate),
                  );
                  const today = new Date(new Date().toDateString());
                  const minDate = new Date(today);
                  minDate.setDate(
                    minDate.getDate() - (props.desdePermisos ? 100 : 0),
                  );
                  if (new Date(dateFind) < minDate) {
                    return mostrarAlerta(
                      props.desdePermisos
                        ? `No se puede seleccionar una fecha anterior a hace 100 días.`
                        : "No se puede seleccionar una fecha pasada o actual.",
                      "error",
                    );
                  }
                  if (
                    (turno === "" ||
                      horasDefault.horaDesde === "" ||
                      horasDefault.horaHasta === "") &&
                    !sinValidacionTurno
                  ) {
                    return mostrarAlerta(
                      `${turno === "" ? "Turno Requerido - " : ""}${
                        horasDefault.horaDesde === ""
                          ? "Hora Desde Requerida - "
                          : ""
                      }${
                        horasDefault.horaHasta === ""
                          ? "Hora Hasta Requerida"
                          : ""
                      }`,
                      "error",
                    );
                  }
                  setData(e);
                }}
                sort
                onFocusedDateChange={setFocusedDate}
                mapDays={(e) => mapDays(e, turno)}
              />
            )}
          </Paper>

          {/* Panel de herramientas */}
          <Paper
            className={clases.surfaceCard}
            elevation={0}
            sx={{
              p: 1.2,
              width: { xs: "100%", sm: 220 },
              maxWidth: { xs: "100%", sm: 240 },
              flexShrink: 0,
            }}
          >
            <Box className={clases.sectionLabel} mb={0.8}>
              <Schedule
                sx={{ fontSize: "0.95rem", color: theme.palette.primary.main }}
              />
              Horas predeterminadas
            </Box>
            <Box display="flex" flexDirection="column" gap={0.8}>
              <MobileTimePicker
                ampm={false}
                label="Desde"
                views={["hours", "minutes"]}
                value={
                  horasDefault.horaDesde
                    ? dayjs(`2000-01-01T${horasDefault.horaDesde}:00`)
                    : null
                }
                onChange={(newValue) =>
                  sethorasDefault({
                    ...horasDefault,
                    horaDesde: newValue ? newValue.format("HH:mm") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { ...timePickerSx, width: "100%", flex: "0 0 auto" },
                  },
                }}
              />
              <MobileTimePicker
                ampm={false}
                label="Hasta"
                views={["hours", "minutes"]}
                value={
                  horasDefault.horaHasta
                    ? dayjs(`2000-01-01T${horasDefault.horaHasta}:00`)
                    : null
                }
                onChange={(newValue) =>
                  sethorasDefault({
                    ...horasDefault,
                    horaHasta: newValue ? newValue.format("HH:mm") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { ...timePickerSx, width: "100%", flex: "0 0 auto" },
                  },
                }}
              />
              <Button
                fullWidth
                disabled={!hayHorasDefault}
                variant="contained"
                color="success"
                size="small"
                startIcon={<DoneAll fontSize="small" />}
                onClick={aplicarHorasATodas}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.66rem",
                  fontWeight: 600,
                  minHeight: 28,
                  boxShadow: "none",
                }}
              >
                Aplicar a todos
              </Button>
            </Box>

            <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />

            <Box className={clases.sectionLabel} mb={0.6}>
              <FilterAlt
                sx={{ fontSize: "0.95rem", color: theme.palette.primary.main }}
              />
              Filtrar por día
            </Box>
            <Box display="flex" flexWrap="wrap" gap="3px">
              {diasSemana.map((d) => (
                <Chip
                  key={d.value}
                  size="small"
                  label={d.label}
                  clickable
                  variant={dia === d.value ? "filled" : "outlined"}
                  color={dia === d.value ? "secondary" : "default"}
                  onClick={() => setseccionActivaDia({ dia: d.value })}
                  sx={{
                    fontSize: "0.6rem",
                    height: "20px",
                    minWidth: "28px",
                    fontWeight: 500,
                    "& .MuiChip-label": { px: 0.5 },
                  }}
                />
              ))}
              <Chip
                size="small"
                label="Todos"
                clickable
                variant={dia === "todos" ? "filled" : "outlined"}
                color="secondary"
                onClick={() => setseccionActivaDia({ dia: "todos" })}
                sx={{
                  fontSize: "0.6rem",
                  height: "20px",
                  fontWeight: 500,
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* ===== Lista de fechas ===== */}
        <Paper className={clases.surfaceCard} elevation={0} sx={{ mt: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <EventNote
                sx={{
                  fontSize: "0.95rem",
                  color: theme.palette.text.secondary,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: theme.palette.text.secondary,
                }}
              >
                Fechas asignadas
              </Typography>
              <Chip
                size="small"
                label={propiedadDB.length}
                color="primary"
                sx={{
                  height: "18px",
                  fontSize: "0.6rem",
                  minWidth: 22,
                  fontWeight: 600,
                }}
              />
              {dia !== "todos" && (
                <Chip
                  size="small"
                  label={`Filtro: ${diaLabel}`}
                  color="secondary"
                  variant="outlined"
                  sx={{ height: "18px", fontSize: "0.58rem" }}
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              py: 0.3,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1,
              ...(esDialog ? {} : { maxHeight: "65vh", overflow: "auto" }),
            }}
          >
            {propiedadDB.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={3}
                sx={{
                  gridColumn: "1 / -1",
                  borderRadius: "10px",
                  border: `1px dashed ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                }}
              >
                <CalendarMonth
                  sx={{
                    fontSize: "1.6rem",
                    color: alpha(theme.palette.text.secondary, 0.4),
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: theme.palette.text.secondary,
                    mt: 0.5,
                    fontWeight: 500,
                  }}
                >
                  No hay fechas asignadas
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    color: alpha(theme.palette.text.secondary, 0.7),
                    mt: 0.2,
                  }}
                >
                  Usa la parametrización o selecciona días en el calendario.
                </Typography>
              </Box>
            ) : (
              propiedadDB
                .sort((a, b) => {
                  return Date.parse(Number(a)) - Date.parse(Number(b));
                })
                .map((itemFecha, index) => {
                  const validacion = new Date(itemFecha.desde).getDay();
                  if (validacion !== dia && dia !== "todos") {
                    return null;
                  }
                  const error =
                    itemFecha.hayDelMismoDia ||
                    itemFecha.hayDelMismoNoche ||
                    itemFecha.hayDelMismoAmbos;
                  const isFocused =
                    crearFecha(focusedDate).substr(0, 10) ===
                    itemFecha.desde.substr(0, 10);
                  const fechaLabelDesde = new Date(
                    Date.parse(itemFecha.desde.substr(0, 10)) +
                      1000 * 60 * 60 * 3,
                  )
                    .toLocaleDateString("es-AR", {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                    })
                    .replace(/^\w/, (c) => c.toUpperCase());
                  const distintoDia =
                    itemFecha.hasta &&
                    itemFecha.hasta.substr(0, 10) !==
                      itemFecha.desde.substr(0, 10);
                  const fechaLabelFin = itemFecha.hasta
                    ? new Date(
                        Date.parse(itemFecha.hasta.substr(0, 10)) +
                          1000 * 60 * 60 * 3,
                      )
                        .toLocaleDateString("es-AR", {
                          weekday: "short",
                          day: "2-digit",
                        })
                        .replace(/^\w/, (c) => c.toUpperCase())
                    : "";
                  const fechaLabel = distintoDia
                    ? `${fechaLabelDesde} → ${fechaLabelFin}`
                    : fechaLabelDesde;
                  const tooltipContent = (
                    <Box>
                      {[
                        { nombre: "Día", ...itemFecha.hayDelMismoDia },
                        { nombre: "Noche", ...itemFecha.hayDelMismoNoche },
                        { nombre: "Ambos", ...itemFecha.hayDelMismoAmbos },
                      ].map((r, i) => (
                        <Box key={i}>
                          {r.desde && r.hasta
                            ? `${r.nombre}: desde ${r.desde} hasta ${r.hasta}`
                            : ""}
                        </Box>
                      ))}
                    </Box>
                  );
                  return (
                    <Box
                      key={index}
                      className={clases.dateRow}
                      sx={{
                        borderLeftColor: isFocused
                          ? theme.palette.primary.main
                          : "transparent",
                        bgcolor: isFocused
                          ? alpha(theme.palette.primary.main, 0.08)
                          : "transparent",
                        ...(error && {
                          bgcolor: alpha(theme.palette.error.main, 0.06),
                          borderLeftColor: alpha(theme.palette.error.main, 0.5),
                        }),
                      }}
                    >
                      <Tooltip
                        title={
                          distintoDia
                            ? "El turno inicia y finaliza en días distintos"
                            : error
                            ? tooltipContent
                            : ""
                        }
                        placement="top"
                        arrow
                        enterDelay={200}
                      >
                        <Chip
                          size="small"
                          label={fechaLabel}
                          color={distintoDia ? "secondary" : "default"}
                          variant={distintoDia ? "filled" : "outlined"}
                          sx={{
                            fontSize: "0.6rem",
                            height: "22px",
                            minWidth: "92px",
                            width: { xs: "100%", sm: "auto" },
                            justifyContent: "flex-start",
                            borderColor: error
                              ? alpha(theme.palette.error.main, 0.4)
                              : theme.palette.divider,
                            fontWeight: 600,
                            bgcolor: distintoDia
                              ? undefined
                              : theme.palette.background.paper,
                            "& .MuiChip-label": { px: 0.6 },
                          }}
                        />
                      </Tooltip>
                      <MobileTimePicker
                        ampm={false}
                        label="Inicio"
                        views={["hours", "minutes"]}
                        value={dayjs(itemFecha.desde)}
                        onChange={(newValue) => {
                          if (!newValue) return;
                          const reconstruido =
                            itemFecha.desde.substring(0, 11) +
                            newValue.format("HH:mm") +
                            ":00";
                          const res = propiedadDB.map((itemEdit, idx) => {
                            if (idx !== index) return itemEdit;
                            const { error } = validarFecha(
                              itemEdit,
                              reconstruido,
                            );
                            if (error) {
                              mostrarAlerta(error, "error");
                              return itemEdit;
                            }
                            return { ...itemEdit, desde: reconstruido };
                          });
                          setitem({ ...item, [textoPropiedadDB]: res });
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            error: !!error,
                            sx: timePickerSx,
                          },
                        }}
                      />
                      <ArrowForward
                        sx={{
                          fontSize: "0.9rem",
                          color: theme.palette.text.disabled,
                        }}
                      />
                      <MobileTimePicker
                        ampm={false}
                        label="Fin"
                        views={["hours", "minutes"]}
                        value={dayjs(itemFecha.hasta)}
                        onChange={(newValue) => {
                          if (!newValue) return;
                          const reconstruido =
                            itemFecha.hasta.substring(0, 11) +
                            newValue.format("HH:mm") +
                            ":00";
                          const res = propiedadDB.map((itemEdit, idx) => {
                            if (idx !== index) return itemEdit;
                            const { error } = validarFecha(
                              itemEdit,
                              reconstruido,
                            );
                            if (error) {
                              mostrarAlerta(error, "error");
                              return itemEdit;
                            }
                            return { ...itemEdit, hasta: reconstruido };
                          });
                          setitem({ ...item, [textoPropiedadDB]: res });
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            error: !!error,
                            sx: timePickerSx,
                          },
                        }}
                      />
                      <Tooltip
                        title="Aplicar horas predeterminadas a esta fecha"
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!hayHorasDefault || !!error}
                            onClick={() => {
                              const res = propiedadDB.map((itemEdit, idx) => {
                                if (idx !== index) return itemEdit;
                                return {
                                  ...itemEdit,
                                  desde:
                                    horasDefault.horaDesde === ""
                                      ? itemEdit.desde
                                      : itemEdit.desde.substring(0, 11) +
                                        horasDefault.horaDesde +
                                        ":00",
                                  hasta:
                                    horasDefault.horaHasta === ""
                                      ? itemEdit.hasta
                                      : itemEdit.hasta.substring(0, 11) +
                                        horasDefault.horaHasta +
                                        ":00",
                                };
                              });
                              setitem({ ...item, [textoPropiedadDB]: res });
                            }}
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              "&:hover:not(:disabled)": {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <CopyAll
                              fontSize="small"
                              color={hayHorasDefault ? "primary" : "disabled"}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })
            )}
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};
